import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

/** Corps JSON envoyé à createEntreprise */
export interface InscriptionEntreprisePayload {
  companyType: 'company' | 'association';
  companyName: string;
  address: string;
  zip: string;
  city: string;
  numberOfEmployees: number;
  firstname: string;
  lastname: string;
  telephone: string;
  email: string;
  comments: string;
  acceptDonnees: boolean;
}

/** Réponse métier attendue pour createEntreprise */
export interface InscriptionEntrepriseApiResponse {
  success: boolean;
  error?: string;
}

export interface Entreprise {
  id: string | number;
  name: string;
  slug?: string;
  logo?: string | null;
  banner?: string | null;
  introduction_text?: string | null;
}

/** Réponse de `getGrandCoeurEntrepriseByCode` : entreprise + sites (tableau ou objet par région). */
export interface EntrepriseWithSites {
  entreprise: Entreprise;
  sites: SitePrelevement[];
  /** Si `false`, l’inscription RDV pour cette entreprise n’est plus ouverte (rétrocompat : absent = actif). */
  action_active?: boolean;
}

export interface SitePrelevement {
  id: string | number;
  name: string;
  region?: string;
  type_plasma?: number;
}

export interface SiteDetails {
  name: string;
  address: string;
  zip: string;
  city: string;
  phone: string;
  info?: string | null;
  informations?: string | null;
  parking?: string | null;
  transports_commun?: string | null;
  accessibilite_pmr?: string | null;
  picture?: string;
  days?: Record<string, string>;
}

export interface RdvDateStepResult {
  site: SiteDetails | null;
  /** Dates réservables (YYYY-MM-DD), excluant fermé / férié si `dates_available` est fourni. */
  availableDates: string[];
  /** Libellés horaires par jour de semaine (clés monday…sunday), si l’API les envoie. */
  days?: Record<string, string>;
}

export interface RdvSlotOption {
  value: string;
  label: string;
  /** `null` = créneau « sans rendez-vous » (affichage désactivé). Omis = réservable (rétrocompatibilité). */
  total?: number | null;
}

export interface RdvSlotsStepResult {
  dateLabel: string;
  withoutRdvHourBegin: string;
  withoutRdvHourEnd: string;
  slots: RdvSlotOption[];
}

/** Corps JSON final pour la création du RDV Grand Cœur */
export interface InscriptionRdvPayload {
  entrepriseId: string | number;
  siteId: string | number;
  type: string;
  date: string;
  time: string;
  lastname: string;
  firstname: string;
  email: string;
  phone: string;
  birthdate: string;
  hasAlreadyDonated: 'OUI' | 'NON';
  hasAlreadyDonatedPlasma: 'OUI' | 'NON';
  parrainage: '' | 'parrain' | 'filleul';
  acceptDonnees: boolean;
}

export interface InscriptionRdvApiResponse {
  success: boolean;
  error?: string;
}

function joinApiUrl(base: string, segment: string): string {
  const b = base.replace(/\/+$/, '');
  const s = segment.replace(/^\/+/, '');
  return `${b}/${s}`;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function readString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Extrait `total` : `null` explicite = sans RDV ; champ absent = réservable (undefined). */
function parseSlotTotal(r: Record<string, unknown>): number | null | undefined {
  if (!('total' in r)) {
    return undefined;
  }
  const v = r['total'];
  if (v === null) {
    return null;
  }
  if (typeof v === 'number' && !Number.isNaN(v)) {
    return v;
  }
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

function coerceId(v: unknown): string | number | null {
  if (v === undefined || v === null) {
    return null;
  }
  if (typeof v === 'string' || typeof v === 'number') {
    return String(v) === '' ? null : v;
  }
  return String(v);
}

@Injectable({ providedIn: 'root' })
export class RdvApiService {
  private readonly http = inject(HttpClient);

  createEntreprise(payload: InscriptionEntreprisePayload): Observable<InscriptionEntrepriseApiResponse> {
    const url = joinApiUrl(environment.apiRdvUrl, 'createGrandCoeurEntreprise');
    return this.http.post<InscriptionEntrepriseApiResponse>(url, payload);
  }

  getEntrepriseBySlug(slug: string): Observable<EntrepriseWithSites> {
    const url = joinApiUrl(environment.apiRdvUrl, 'getGrandCoeurEntrepriseByCode');
    return this.http
      .post<unknown>(url, { slug })
      .pipe(map((body) => this.parseEntrepriseWithSites(body)));
  }

  getDatesBySite(
    siteId: string | number,
    lang: string,
    rdvType?: 'sang' | 'plasma',
  ): Observable<RdvDateStepResult> {
    const url = joinApiUrl(environment.apiRdvUrl, 'listGrandCoeurRdvDatesBySite');
    return this.http
      .post<unknown>(url, { siteId, lang })
      .pipe(map((body) => this.parseDatesResponse(body, rdvType)));
  }

  getSlotsBySiteAndDate(siteId: string | number, date: string, type: string): Observable<RdvSlotsStepResult> {
    const url = joinApiUrl(environment.apiRdvUrl, 'getGrandCoeurRdvSlotsBySiteAndDate');
    return this.http.post<unknown>(url, { siteId, date, type }).pipe(map((body) => this.parseSlotsResponse(body)));
  }

  createRdv(payload: InscriptionRdvPayload): Observable<InscriptionRdvApiResponse> {
    const url = joinApiUrl(environment.apiRdvUrl, 'createGrandCoeurRdv');
    return this.http.post<InscriptionRdvApiResponse>(url, payload);
  }

  private parseEntrepriseWithSites(body: unknown): EntrepriseWithSites {
    const o = asRecord(body);
    if (!o || o['success'] === false) {
      throw new Error(readString(o?.['error']) || 'Entreprise introuvable.');
    }

    const rawEnt = o['entreprise'] ?? o['company'] ?? o['data'];
    const r = asRecord(rawEnt);
    if (!r) {
      throw new Error('Désolé, nous ne trouvons pas cette entreprise.');
    }
    const id = coerceId(r['id'] ?? r['entrepriseId']);
    const name = readString(r['name'] ?? r['nom'] ?? r['label']);
    if (id === null || !name) {
      throw new Error('Réponse API : id ou nom d’entreprise manquant.');
    }
    const slugVal = readString(r['slug'] ?? r['slug_entreprise'] ?? r['code_entreprise']);
    const logoRaw = r['logo'] ?? r['logoUrl'] ?? r['picture'] ?? r['image'];
    const logo =
      typeof logoRaw === 'string' && logoRaw.trim() ? logoRaw.trim() : null;

    const bannerRaw = r['banner'] ?? r['bannerUrl'] ?? r['picture'] ?? r['image'];
    const banner =
      typeof bannerRaw === 'string' && bannerRaw.trim() ? bannerRaw.trim() : null;

    const descriptionRaw = r['introduction_text'] ?? r['description_entreprise'] ?? r['description_entreprise'];
    const introduction_text =
      typeof descriptionRaw === 'string' && descriptionRaw.trim() ? descriptionRaw.trim() : null;

    const entreprise: Entreprise = {
      id,
      name,
      slug: slugVal || undefined,
      logo,
      banner,
      introduction_text,
    };

    const rawSites = o['sites'];
    const sites = this.normalizeSitesPayload(rawSites);

    const rawAction = o['action_active'];
    const action_active =
      rawAction === false || rawAction === 0 || rawAction === '0' || rawAction === 'false'
        ? false
        : rawAction === true || rawAction === 1 || rawAction === '1' || rawAction === 'true'
          ? true
          : undefined;

    return { entreprise, sites, action_active };
  }

  /**
   * `sites` peut être un tableau ou un objet { "Région": [ { site }, ... ], ... }.
   */
  private normalizeSitesPayload(raw: unknown): SitePrelevement[] {
    if (Array.isArray(raw)) {
      return this.mapSiteItems(raw);
    }
    const obj = asRecord(raw);
    if (!obj) {
      return [];
    }
    const out: SitePrelevement[] = [];
    for (const [regionKey, value] of Object.entries(obj)) {
      if (!Array.isArray(value)) {
        continue;
      }
      out.push(...this.mapSiteItems(value, regionKey));
    }
    return out;
  }

  private mapSiteItems(raw: unknown[], fallbackRegion?: string): SitePrelevement[] {
    const out: SitePrelevement[] = [];
    for (const item of raw) {
      const r = asRecord(item);
      if (!r) {
        continue;
      }
      const id = coerceId(r['id'] ?? r['site_id']);
      const name = readString(r['name'] ?? r['nom'] ?? r['label']);
      const regionFromItem = readString(r['region'] ?? r['region_name'] ?? r['zone']);
      const region = regionFromItem || fallbackRegion || '';
      const type_plasma =
        typeof r['type_plasma'] === 'number' ? r['type_plasma'] : Number(r['type_plasma']) || undefined;
      if (id !== null && name) {
        out.push({ id, name, region: region || undefined, type_plasma });
      }
    }
    return out;
  }

  private parseDatesResponse(body: unknown, rdvType?: 'sang' | 'plasma'): RdvDateStepResult {
    const o = asRecord(body);
    if (!o) {
      throw new Error('Réponse invalide.');
    }
    if (o['success'] === false) {
      throw new Error(readString(o['error']) || 'Aucune date disponible pour ce lieu.');
    }

    let dates: string[] = [];
    const datesAvailable = o['dates_available'];
    if (datesAvailable != null) {
      dates = this.flattenBookableDatesFromCalendar(datesAvailable, rdvType);
    }
    if (dates.length === 0) {
      const rawDates = o['availableDates'] ?? o['dates'];
      const dataVal = o['data'];
      if (Array.isArray(rawDates)) {
        dates = rawDates.map((d) => String(d).slice(0, 10));
      } else if (Array.isArray(dataVal)) {
        dates = dataVal.map((d) => String(d).slice(0, 10));
      }
    }

    const siteBase = this.parseSiteDetails(o['site'] ?? o['siteInfo'] ?? o['centre']);
    const days = this.parseDaysLabels(o['days']);
    const site =
      siteBase && days && Object.keys(days).length > 0 ? { ...siteBase, days } : siteBase;

    return { site, availableDates: [...new Set(dates)].sort(), days };
  }

  /**
   * Parcourt `dates_available` : { "YYYY-MM": { "YYYY-MM-DD": meta, ... } | [] }.
   * Exclut les jours avec template_id -1 (fermé) ou -2 (férié), ou libellé type fermé.
   */
  private flattenBookableDatesFromCalendar(root: unknown, rdvType?: 'sang' | 'plasma'): string[] {
    const out: string[] = [];
    const months = asRecord(root);
    if (!months) {
      return out;
    }

    const monthKeys = Object.keys(months).sort();
    for (const monthKey of monthKeys) {
      const monthVal = months[monthKey];
      if (Array.isArray(monthVal)) {
        for (const item of monthVal) {
          if (typeof item === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(item)) {
            out.push(item);
          }
        }
        continue;
      }
      const daysInMonth = asRecord(monthVal);
      if (!daysInMonth) {
        continue;
      }
      const dayKeys = Object.keys(daysInMonth).sort();
      for (const dateKey of dayKeys) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
          continue;
        }
        const meta = asRecord(daysInMonth[dateKey]);
        if (
          !meta ||
          !this.isCalendarDayBookable(meta)
        ) {
          continue;
        }
        out.push(dateKey);
      }
    }
    return out;
  }

  private isCalendarDayBookable(meta: Record<string, unknown>): boolean {
    const tidRaw = meta['template_id'];
    const tid = tidRaw != null ? String(tidRaw).trim() : '';
    if (tid === '-1' || tid === '-2') {
      return false;
    }
    const label = readString(meta['template_name'])
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toLowerCase();
    if (
      label.includes('ferme') ||
      label.includes('ferie') ||
      label.includes('holiday')
    ) {
      return false;
    }
    return true;
  }

  /**
   * Si `rdvType` est précisé et que l’API fournit le compteur correspondant sur le jour,
   * n’accepte le jour que si ce compteur est strictement positif.
   * Si le champ est absent (réponse héritée), le jour reste éligible comme avant.
   */
  private isCalendarDayAvailableForDonationKind(
    meta: Record<string, unknown>,
    rdvType: 'sang' | 'plasma' | undefined,
  ): boolean {
    if (rdvType !== 'sang' && rdvType !== 'plasma') {
      return true;
    }
    const key = rdvType === 'sang' ? 'available_sang' : 'available_plasma';
    if (!(key in meta)) {
      return true;
    }
    const raw = meta[key];
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(n)) {
      return true;
    }
    return n > 0;
  }

  private parseDaysLabels(v: unknown): Record<string, string> | undefined {
    const rec = asRecord(v);
    if (!rec) {
      return undefined;
    }
    const out: Record<string, string> = {};
    for (const [k, val] of Object.entries(rec)) {
      if (typeof val === 'string') {
        out[k] = val;
      }
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }

  private parseSiteDetails(v: unknown): SiteDetails | null {
    const r = asRecord(v);
    if (!r) {
      return null;
    }
    return {
      name: readString(r['name'] ?? r['nom']),
      address: readString(r['address'] ?? r['adresse'] ?? r['addressStreet']),
      zip: readString(r['zip'] ?? r['code_postal']),
      city: readString(r['city'] ?? r['localite'] ?? r['ville']),
      phone: readString(r['phone'] ?? r['telephone'] ?? r['tel']),
      info:
        typeof r['info'] === 'string'
          ? r['info']
          : r['info'] == null
            ? null
            : String(r['info']),
      informations:
        typeof r['informations'] === 'string'
          ? r['informations']
          : r['informations'] == null
            ? null
            : String(r['informations']),
      parking:
        typeof r['parking'] === 'string'
          ? r['parking']
          : r['parking'] == null
            ? null
            : String(r['parking']),
      transports_commun:
        typeof r['transports_commun'] === 'string'
          ? r['transports_commun']
          : r['transports_commun'] == null
            ? null
            : String(r['transports_commun']),
      accessibilite_pmr:
        typeof r['accessibilite_pmr'] === 'string'
          ? r['accessibilite_pmr']
          : r['accessibilite_pmr'] == null
            ? null
            : String(r['accessibilite_pmr']),
      picture: readString(r['picture'] ?? r['photo'] ?? r['image']),
    };
  }

  private parseSlotsResponse(body: unknown): RdvSlotsStepResult {
    const o = asRecord(body);
    if (!o || o['success'] === false) {
      throw new Error(readString(o?.['error']) || 'Aucun créneau disponible.');
    }
    const dateLabel = readString(o['date'] ?? o['dateLabel'] ?? o['date_display'] ?? o['dateSelect']);
    const withoutRdvHourBegin = readString(o['withoutRdvHourBegin'] ?? o['horaire_debut_sans_rdv']);
    const withoutRdvHourEnd = readString(o['withoutRdvHourEnd'] ?? o['horaire_fin_sans_rdv']);
    const raw = o['slots'] ?? o['plages'] ?? o['horaires'] ?? o['data'];
    const slots: RdvSlotOption[] = [];
    if (Array.isArray(raw)) {
      for (const item of raw) {
        const r = asRecord(item);
        if (r) {
          const availRaw = r['available'] ?? r['disponible'];
          if (availRaw !== undefined && availRaw !== null) {
            const avail = typeof availRaw === 'number' ? availRaw : Number(availRaw);
            if (!Number.isNaN(avail) && avail <= 0) {
              continue;
            }
          }
          const value = readString(r['value'] ?? r['id'] ?? r['horaire'] ?? r['time']);
          const label = readString(r['label'] ?? r['libelle'] ?? r['text'] ?? value);
          if (value) {
            const total = parseSlotTotal(r);
            slots.push({ value, label: label || value, total });
          }
        } else if (typeof item === 'string') {
          slots.push({ value: item, label: item });
        }
      }
    }
    return { dateLabel: dateLabel || '', withoutRdvHourBegin: withoutRdvHourBegin || '', withoutRdvHourEnd: withoutRdvHourEnd || '', slots: slots };
  }
}
